import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ArrowLeft, FileText, Download, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

import { API_URL } from '../utils/api';

const ResponsesPage = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const [formRes, responsesRes] = await Promise.all([
        axios.get(`${API_URL}/api/forms/${formId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/responses/${formId}`, { withCredentials: true })
      ]);
      setForm(formRes.data);
      // Ensure responses is always an array
      const resData = Array.isArray(responsesRes.data) ? responsesRes.data : [];
      setResponses(resData);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err.message;
      if (status === 403) {
        setError('You do not have permission to view responses for this form.');
      } else if (status === 404) {
        setError('Form not found.');
      } else {
        setError(`Failed to load responses: ${detail}`);
      }
      toast.error('Failed to load responses');
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Expand grid fields into separate columns (one per row in the grid)
  const getExpandedColumns = () => {
    const fields = (form?.fields || []).filter(f => f.type !== 'section');
    const columns = [];
    for (const field of fields) {
      if ((field.type === 'multiple_choice_grid' || field.type === 'checkbox_grid') && field.rows && field.rows.length > 0) {
        for (const rowLabel of field.rows) {
          columns.push({ fieldId: field.id, label: `${field.label} [${rowLabel}]`, rowKey: rowLabel, type: field.type, isGridSub: true });
        }
      } else {
        columns.push({ fieldId: field.id, label: field.label, type: field.type, isGridSub: false });
      }
    }
    return columns;
  };

  const handleExportCSV = () => {
    if (!form || !responses.length) return;

    const columns = getExpandedColumns();
    const headers = [];
    if (form.collect_email) headers.push('Email');
    if (form.is_quiz) headers.push('Score');
    headers.push(...columns.map(c => c.label));
    headers.push('Submitted At');

    const rows = responses.map(response => {
      const answers = response.answers || {};
      const row = [];
      if (form.collect_email) row.push(response.respondent_email || '');
      if (form.is_quiz) row.push(response.quiz_score != null ? `${response.quiz_score}/${response.quiz_total}` : '');
      row.push(...columns.map(col => {
        try {
          const answer = answers[col.fieldId];
          if (answer === null || answer === undefined) return '';
          // Grid sub-column: extract specific row value
          if (col.isGridSub && typeof answer === 'object' && !Array.isArray(answer)) {
            const val = answer[col.rowKey];
            if (val === null || val === undefined) return '';
            if (Array.isArray(val)) return val.join(', ');
            return String(val);
          }
          // File upload - single or multi
          if (col.type === 'file') {
            if (Array.isArray(answer)) {
              return answer.map(f => f.filename || f.file_id).join(', ');
            }
            if (typeof answer === 'object' && answer.file_id) {
              return answer.filename || answer.file_id;
            }
          }
          // Date fields — convert old ISO dates to local YYYY-MM-DD
          if (col.type === 'date' && typeof answer === 'string') {
            // New format: already YYYY-MM-DD
            if (/^\d{4}-\d{2}-\d{2}$/.test(answer)) return answer;
            // Old format: ISO datetime like "2026-05-11T18:30:00.000Z" → convert to local date
            if (answer.includes('T')) {
              try {
                const d = new Date(answer);
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${y}-${m}-${day}`;
              } catch { return answer; }
            }
            return answer;
          }
          if (Array.isArray(answer)) return answer.join(', ');
          if (typeof answer === 'object') {
            if (answer.filename) return answer.filename;
            try { return JSON.stringify(answer); } catch { return String(answer); }
          }
          return String(answer);
        } catch {
          return '';
        }
      }));
      // Format submitted_at in IST for CSV
      const rawSubmitted = response.submitted_at || '';
      let formattedSubmitted = rawSubmitted;
      if (rawSubmitted) {
        try {
          const d = new Date(rawSubmitted);
          formattedSubmitted = d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
        } catch { /* keep raw */ }
      }
      row.push(formattedSubmitted);
      return row;
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${form.title || 'form'}_responses.csv`;
    link.click();
    toast.success('CSV exported');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  const formatAnswer = (answer, field) => {
    try {
      if (answer === null || answer === undefined || answer === '') return '-';
      
      // File upload - show filename with link (multi-file support)
      if (field?.type === 'file') {
        if (Array.isArray(answer)) {
          return (
            <div className="flex flex-col gap-1">
              {answer.map((f, i) => (
                <a key={f.file_id || i} href={`${API_URL}/api/files/${f.file_id}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline text-sm">
                  {f.filename || 'File'}
                </a>
              ))}
            </div>
          );
        }
        if (typeof answer === 'object' && answer !== null && answer.file_id) {
          return (
            <a href={`${API_URL}/api/files/${answer.file_id}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">
              {answer.filename || 'File'}
            </a>
          );
        }
      }
      
      // Grid types - flatten to readable string
      if ((field?.type === 'multiple_choice_grid' || field?.type === 'checkbox_grid') && typeof answer === 'object' && answer !== null && !Array.isArray(answer)) {
        return Object.entries(answer).map(([row, val]) => {
          const valStr = Array.isArray(val) ? val.join(', ') : String(val || '');
          return `${row}: ${valStr}`;
        }).join(' | ');
      }
      
      // Arrays (checkbox, etc)
      if (Array.isArray(answer)) {
        return answer.map(a => String(a ?? '')).join(', ');
      }
      
      // Objects (catch-all for any unhandled dict types) - MUST be before string return
      if (typeof answer === 'object' && answer !== null) {
        try { return JSON.stringify(answer); } catch { return '[Object]'; }
      }
      
      // Date strings
      if (typeof answer === 'string' && answer.includes('-')) {
        // YYYY-MM-DD format (date-only, no timezone)
        if (/^\d{4}-\d{2}-\d{2}$/.test(answer)) {
          const [y, m, d] = answer.split('-').map(Number);
          try { return format(new Date(y, m - 1, d), 'MMM d, yyyy'); } catch { return answer; }
        }
        // ISO datetime format (old responses)
        if (answer.includes('T')) {
          try { return format(new Date(answer), 'MMM d, yyyy'); } catch { return answer; }
        }
      }
      
      return String(answer);
    } catch {
      return '[Error displaying]';
    }
  };

  const safeGetAnswer = (response, fieldId, field) => {
    try {
      const answers = response?.answers;
      if (!answers || typeof answers !== 'object') return '-';
      return formatAnswer(answers[fieldId], field);
    } catch {
      return '[Error]';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4" data-testid="responses-error">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2 font-['Outfit']">Unable to Load Responses</h3>
            <p className="text-slate-500 text-sm mb-6">{error}</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="error-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
              </Button>
              <Button onClick={fetchData} className="bg-indigo-600 hover:bg-indigo-700" data-testid="error-retry-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const visibleFields = (form?.fields || []).filter(f => f.type !== 'section');
  const expandedColumns = getExpandedColumns();

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="responses-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-slate-600"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="border-l border-slate-200 h-6"></div>
              <h1 className="text-lg font-semibold font-['Outfit'] text-slate-900 truncate max-w-md">
                {form?.title || 'Form'} - Responses
              </h1>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                disabled={!responses.length}
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Card */}
        <Card className="mb-6" data-testid="stats-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Outfit']">Response Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-8">
              <div>
                <p className="text-3xl font-semibold text-indigo-600" data-testid="response-count">{responses.length}</p>
                <p className="text-sm text-slate-500">Total Responses</p>
              </div>
              <div>
                <p className="text-3xl font-semibold text-slate-900">{expandedColumns.length}</p>
                <p className="text-sm text-slate-500">Questions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Responses Table */}
        {responses.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-300 bg-white/50">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2 font-['Outfit']">No responses yet</h3>
              <p className="text-slate-500 text-center max-w-sm">
                Share your form link to start collecting responses
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="responses-table-card">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium text-slate-700">#</TableHead>
                      {form?.collect_email && (
                        <TableHead className="font-medium text-slate-700 min-w-[200px]">Email</TableHead>
                      )}
                      {form?.is_quiz && (
                        <TableHead className="font-medium text-slate-700 min-w-[100px]">Score</TableHead>
                      )}
                      {expandedColumns.map((col, idx) => (
                        <TableHead key={`${col.fieldId}-${idx}`} className="font-medium text-slate-700 min-w-[150px]">
                          {col.label}
                        </TableHead>
                      ))}
                      <TableHead className="font-medium text-slate-700 min-w-[180px]">Submitted At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {responses.map((response, index) => (
                      <TableRow key={response.id || index} data-testid={`response-row-${index}`}>
                        <TableCell className="font-medium text-slate-600">{index + 1}</TableCell>
                        {form?.collect_email && (
                          <TableCell className="text-slate-700 text-sm">{response.respondent_email || '-'}</TableCell>
                        )}
                        {form?.is_quiz && (
                          <TableCell className="text-slate-700 text-sm font-medium">
                            {response.quiz_score != null ? `${response.quiz_score}/${response.quiz_total}` : '-'}
                          </TableCell>
                        )}
                        {expandedColumns.map((col, idx) => (
                          <TableCell key={`${col.fieldId}-${idx}`} className="text-slate-700">
                            {col.isGridSub
                              ? (() => {
                                  const answer = response?.answers?.[col.fieldId];
                                  if (!answer || typeof answer !== 'object' || Array.isArray(answer)) return '-';
                                  const val = answer[col.rowKey];
                                  if (val === null || val === undefined) return '-';
                                  if (Array.isArray(val)) return val.join(', ');
                                  return String(val);
                                })()
                              : safeGetAnswer(response, col.fieldId, visibleFields.find(f => f.id === col.fieldId))
                            }
                          </TableCell>
                        ))}
                        <TableCell className="text-slate-500 text-sm">
                          {formatDate(response.submitted_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ResponsesPage;
